import {
  updateStoreWhatsappRequestSchema,
  type StoreContact,
  type StoreMail,
} from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  fill,
  Loading,
  NO_ERRORS,
  PageHeader,
  returnToPrevious,
  StoreMailCard,
  textOrNull,
  TextField,
  type FieldErrors,
  type StoreMailWords,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState, type SyntheticEvent } from 'react';
import { keys, platformApi, tenantContactQuery, tenantQuery } from '../api/platform';
import { hasCapability, sessionQuery } from '../api/session';
import { he } from '../i18n/he';
import { mergeErrors, parseWith } from '../lib/forms';
import { errorMessage } from '../lib/format';

const MAIL_WORDS: StoreMailWords = {
  ...he.storeContact,
  title: he.storeContact.mailTitle,
  intro: he.storeContact.mailIntro,
  none: he.storeContact.mailNone,
  failed: (address, when) => fill(he.storeContact.failed, { address, when }),
  cancel: he.common.cancel,
};
const MAIL_CHECKS = { parseWith, mergeErrors };

/**
 * How shoppers and their requests reach one store, set by the platform staff for the store
 * (adr/0028): its WhatsApp number and the Gmail account its quote requests are mailed to. The
 * store's owner has the same two things in the store's own settings.
 */
export function TenantContactPage() {
  const { tenantId } = useParams({ from: '/app/tenants/$tenantId/contact' });
  const session = useQuery(sessionQuery);
  const can = (capability: 'platform.tenants.read' | 'platform.tenants.create') =>
    session.data !== undefined && hasCapability(session.data, capability);
  const canRead = can('platform.tenants.read');
  const canManage = can('platform.tenants.create');
  // Only for the store's name in the title: the screen works without it.
  const tenant = useQuery({ ...tenantQuery(tenantId), enabled: canRead });
  const contact = useQuery({ ...tenantContactQuery(tenantId), enabled: canRead });
  const name = tenant.data?.tenant.displayName;
  return (
    <>
      <Link
        to="/tenants/$tenantId"
        params={{ tenantId }}
        onClick={returnToPrevious}
        className="mb-2 inline-block text-sm text-brand-700 hover:underline"
      >
        {he.storeContact.back}
      </Link>
      <PageHeader
        title={name === undefined ? he.storeContact.title : `${he.storeContact.title}: ${name}`}
      />
      {canRead ? (
        <div className="flex flex-col gap-6">
          <p className="text-sm text-slate-600">{he.storeContact.intro}</p>
          {canManage ? null : <Alert tone="info">{he.storeContact.readOnly}</Alert>}
          {contact.isPending ? <Loading /> : null}
          {contact.isError ? (
            <Alert>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span>
                  {errorMessage(contact.error, {
                    FORBIDDEN: he.storeContact.forbidden,
                    NOT_FOUND: he.storeContact.notFound,
                  })}
                </span>
                <Button
                  tone="secondary"
                  busy={contact.isFetching}
                  onClick={() => {
                    void contact.refetch();
                  }}
                >
                  {he.common.retry}
                </Button>
              </div>
            </Alert>
          ) : null}
          {contact.data === undefined ? null : (
            <>
              <WhatsappCard
                key={`whatsapp-${tenantId}`}
                tenantId={tenantId}
                contact={contact.data}
                canManage={canManage}
              />
              <MailCard
                key={`mail-${tenantId}`}
                tenantId={tenantId}
                mail={contact.data.mail}
                canManage={canManage}
              />
            </>
          )}
        </div>
      ) : (
        <Alert>{he.storeContact.forbidden}</Alert>
      )}
    </>
  );
}

/** Keeps the screen, the store's page and its history in step with a change made here. */
function useContactCache(tenantId: string) {
  const queryClient = useQueryClient();
  const key = keys.tenantContact(tenantId);
  const changed = () => queryClient.invalidateQueries({ queryKey: keys.audit(tenantId) });
  return {
    contact: (updated: StoreContact) => {
      queryClient.setQueryData(key, updated);
      void changed();
    },
    mail: (mail: StoreMail) => {
      queryClient.setQueryData(key, (current: StoreContact | undefined) =>
        current === undefined ? current : { ...current, mail },
      );
      void changed();
    },
  };
}

function WhatsappCard({
  tenantId,
  contact,
  canManage,
}: {
  tenantId: string;
  contact: StoreContact;
  canManage: boolean;
}) {
  const cache = useContactCache(tenantId);
  const [text, setText] = useState(contact.whatsapp ?? '');
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);
  const save = useMutation({
    mutationFn: (whatsapp: string | null) => platformApi.setStoreWhatsapp(tenantId, { whatsapp }),
    onSuccess: (updated) => {
      cache.contact(updated);
      // The field shows the number as it was kept.
      setText(updated.whatsapp ?? '');
    },
  });
  const errors = mergeErrors(localErrors, save.error);
  const unchanged = textOrNull(text) === contact.whatsapp;

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const parsed = parseWith(updateStoreWhatsappRequestSchema, { whatsapp: textOrNull(text) });
    if (!parsed.ok) {
      save.reset();
      setLocalErrors(parsed.errors);
      return;
    }
    setLocalErrors(NO_ERRORS);
    save.mutate(parsed.value.whatsapp);
  };

  return (
    <Card title={he.storeContact.whatsapp}>
      <form
        onSubmit={submit}
        noValidate
        className="flex flex-col gap-4"
        data-testid="store-whatsapp"
      >
        <TextField
          label={he.storeContact.whatsapp}
          name="whatsapp"
          type="tel"
          dir="ltr"
          autoComplete="off"
          maxLength={20}
          className="max-w-md"
          hint={he.storeContact.whatsappHint}
          data-testid="store-whatsapp-input"
          value={text}
          error={errors['whatsapp']}
          disabled={!canManage || save.isPending}
          onChange={(event) => {
            setText(event.target.value);
            setLocalErrors(NO_ERRORS);
            if (!save.isIdle) save.reset();
          }}
        />
        <p className="text-sm text-slate-600" data-testid="store-whatsapp-fallback">
          {contact.phone === null ? (
            he.storeContact.whatsappNone
          ) : (
            <>
              {he.storeContact.whatsappFallback} <bdi dir="ltr">{contact.phone}</bdi>
            </>
          )}
        </p>
        {save.isSuccess ? (
          <div data-testid="store-whatsapp-saved">
            <Alert tone="success">{he.storeContact.whatsappSaved}</Alert>
          </div>
        ) : null}
        {save.isError && errors['whatsapp'] === undefined ? (
          <Alert>{errorMessage(save.error)}</Alert>
        ) : null}
        {canManage ? (
          <div>
            <Button
              type="submit"
              busy={save.isPending}
              disabled={unchanged}
              data-testid="store-whatsapp-save"
            >
              {he.common.save}
            </Button>
          </div>
        ) : null}
      </form>
    </Card>
  );
}

/** The store's Gmail account: the card of the store's own admin, over the platform's routes. */
function MailCard({
  tenantId,
  mail,
  canManage,
}: {
  tenantId: string;
  mail: StoreMail;
  canManage: boolean;
}) {
  const cache = useContactCache(tenantId);
  return (
    <StoreMailCard
      mail={mail}
      canWrite={canManage}
      words={MAIL_WORDS}
      actions={{
        connect: (body) => platformApi.connectStoreMail(tenantId, body),
        disconnect: () => platformApi.disconnectStoreMail(tenantId),
        sendTest: () => platformApi.testStoreMail(tenantId),
      }}
      errorMessage={errorMessage}
      checks={MAIL_CHECKS}
      onChange={cache.mail}
    />
  );
}
